import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, unref as _unref } from "vue"

const _hoisted_1 = { class: "module binary-logic-module" }
const _hoisted_2 = { class: "content" }
const _hoisted_3 = { class: "display" }
const _hoisted_4 = { class: "display" }

import "@style/modules/binary-logic.scss";
import ModuleStatusIndicator from "@src/components/ModuleStatusIndicator.vue";
import { computed, onMounted, ref } from "vue";
import defineModuleState, {
  ModuleEmits,
  ModuleProps,
} from "@src/composables/defineModuleState";
import defineDebugState from "@src/composables/defineDebugState";
import Frame from "@src/components/Frame.vue";
import Keycap from "@src/components/Keycap.vue";
import RotarySwitch from "@src/components/RotarySwitch.vue";
import _ from "lodash";
import ScrewHead from "@src/components/ScrewHead.vue";
import BinaryLogicPattern from "@src/modules/BinaryLogic/structures/BinaryLogicPattern";
import { getLevel } from "@src/modules/BinaryLogic/helpers/levels";

enum OperationOption {
  AND,
  OR,
  NAND,
  XOR,
}

const MATRIX_WIDTH = 4;


export default /*#__PURE__*/_defineComponent({
  __name: 'BinaryLogicModule',
  props: ModuleProps,
  emits: ModuleEmits,
  setup(__props, { emit }) {

const props = __props;



const state = defineModuleState(props, emit);
const debugState = defineDebugState();

state.onArmed(armModule);
state.onReady(restartModule);
state.onFailed(freezeModule);
const strikeNumber = ref<number>(0);

const selectedParameter = ref<number>(0);
const selectedOperation = ref<number>(0);
const bufferMatrix = ref<Array<boolean>>(new Array(4 * 4).fill(false));
const outputMatrix = ref<Array<boolean>>(new Array(4 * 4).fill(false));

let level: BinaryLogicPattern = new BinaryLogicPattern([], []);

// //prettier-ignore
// const solution = [
//         1, 0, 0, 0,
//         0, 0, 0, 0,
//         0, 0, 0, 0,
//         0, 0, 0, 0,
//     ];
//
// //prettier-ignore
// const parameters = [
//     [
//         0, 1, 0, 1,
//         1, 0, 1, 0,
//         0, 1, 0, 1,
//         1, 0, 1, 0,
//     ],
//     [
//         0, 0, 0, 0,
//         1, 1, 1, 1,
//         0, 0, 0, 0,
//         1, 1, 1, 1,
//     ],
//     [
//         1, 1, 1, 1,
//         1, 1, 1, 1,
//         0, 0, 0, 0,
//         0, 0, 0, 0,
//     ],
//     [
//         1, 1, 0, 0,
//         1, 1, 0, 0,
//         0, 0, 1, 1,
//         0, 0, 1, 1,
//     ],
// ];

function armModule(): void {
  level = getLevel(props.difficulty, props.serialNumber);
  selectedParameter.value = 0;
  updateBufferMatrix(level.parameters[selectedParameter.value]);
  initOutputMatrix();
}

function restartModule(): void {}

function freezeModule(): void {}

function updateBufferMatrix(data: Array<boolean>): void {
  if (state.isArmed.value) {
    for (let i = 0; i < bufferMatrix.value.length; i++) {
      bufferMatrix.value[i] = data[i];
    }
  }
}

function prevParameter(): void {
  if (state.isArmed.value) {
    if (selectedParameter.value == 0) {
      selectedParameter.value = level.parameters.length - 1;
    } else {
      selectedParameter.value = selectedParameter.value - 1;
    }

    updateBufferMatrix(level.parameters[selectedParameter.value]);
  }
}

function nextParameter(): void {
  if (state.isArmed.value) {
    selectedParameter.value =
      (selectedParameter.value + 1) % level.parameters.length;

    updateBufferMatrix(level.parameters[selectedParameter.value]);
  }
}

function selectOperator(op: string): void {
  switch (op) {
    case "AND":
      selectedOperation.value = OperationOption.AND;
      return;
    case "OR":
      selectedOperation.value = OperationOption.OR;
      return;
    case "XOR":
      selectedOperation.value = OperationOption.XOR;
      return;
    case "NAND":
      selectedOperation.value = OperationOption.NAND;
      return;
  }
}

function rotateBuffer(): void {
  if (state.isArmed.value) {
    const data = new Array(MATRIX_WIDTH * MATRIX_WIDTH).fill(0);

    for (let y = 0; y < MATRIX_WIDTH; y++) {
      for (let x = 0; x < MATRIX_WIDTH; x++) {
        const index = y * MATRIX_WIDTH + x;
        const index2 = x * MATRIX_WIDTH + (MATRIX_WIDTH - y - 1);

        data[index] = bufferMatrix.value[index2];
      }
    }

    updateBufferMatrix(data);
  }
}

function mirrorBuffer(): void {
  if (state.isArmed.value) {
    const data = new Array(MATRIX_WIDTH * MATRIX_WIDTH).fill(0);

    for (let y = 0; y < MATRIX_WIDTH; y++) {
      for (let x = 0; x < MATRIX_WIDTH; x++) {
        const index = y * MATRIX_WIDTH + x;
        const index2 = y * MATRIX_WIDTH + (MATRIX_WIDTH - 1 - x);

        data[index] = bufferMatrix.value[index2];
      }
    }

    updateBufferMatrix(data);
  }
}

function operator(a: boolean, b: boolean): boolean {
  const A = Boolean(a);
  const B = Boolean(b);
  let result: boolean = false;
  switch (selectedOperation.value) {
    case OperationOption.AND:
      result = A && B;
      break;
    case OperationOption.OR:
      result = A || B;
      break;
    case OperationOption.XOR:
      result = A !== B;
      break;
    case OperationOption.NAND:
      result = !(A && B);
      break;
  }

  return Boolean(result);
}

function applyBuffer(): void {
  if (state.isArmed.value) {
    for (let i = 0; i < bufferMatrix.value.length; i++) {
      outputMatrix.value[i] = operator(
        outputMatrix.value[i],
        bufferMatrix.value[i],
      );
    }

    checkForSolution();
  }
}

function initOutputMatrix(): void {
  for (let i = 0; i < outputMatrix.value.length; i++) {
    outputMatrix.value[i] = _.random(0, 1);
  }
}

function checkForSolution(): void {
  if (level.isEqual(outputMatrix.value)) {
    state.emitDisarmed();
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(RotarySwitch, {
        top: 165,
        left: 220,
        text: "OPERATOR",
        values: [
          {
            value: 'AND',
            text: 'AND',
          },
          {
            value: 'OR',
            text: 'OR',
          },
          {
            value: 'XOR',
            text: 'XOR',
          },
          {
            value: 'NAND',
            text: 'NAND',
          },
        ],
        onChange: selectOperator
      }),
      _createVNode(Frame, {
        text: "BUFFER",
        top: 20,
        left: 20,
        width: 125,
        height: 125,
        radius: "8px"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_3, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bufferMatrix.value, (val) => {
              return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(['active-' + Number(val)])
              }, null, 2 /* CLASS */))
            }), 256 /* UNKEYED_FRAGMENT */))
          ])
        ]),
        _: 1 /* STABLE */
      }),
      _createVNode(Frame, {
        text: "OUTPUT",
        top: 20,
        left: 240,
        width: 125,
        height: 125,
        radius: "8px"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(outputMatrix.value, (val) => {
              return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(['active-' + Number(val)])
              }, null, 2 /* CLASS */))
            }), 256 /* UNKEYED_FRAGMENT */))
          ])
        ]),
        _: 1 /* STABLE */
      }),
      _createVNode(Frame, {
        top: 170,
        left: 20,
        width: 100,
        height: 100,
        radius: "8px",
        "has-border": false
      }, {
        default: _withCtx(() => [
          _createVNode(Keycap, {
            width: "60px",
            height: "50px",
            top: "0",
            left: "0",
            onClick: prevParameter
          }, {
            default: _withCtx(() => [
              _createTextVNode("◂")
            ]),
            _: 1 /* STABLE */
          }),
          _createVNode(Keycap, {
            width: "60px",
            height: "50px",
            top: "0",
            left: "70px",
            onClick: nextParameter
          }, {
            default: _withCtx(() => [
              _createTextVNode("▸")
            ]),
            _: 1 /* STABLE */
          }),
          _createVNode(Keycap, {
            width: "60px",
            height: "50px",
            top: "60px",
            left: "0",
            "font-size": "11px",
            onClick: mirrorBuffer
          }, {
            default: _withCtx(() => [
              _createTextVNode("MIRROR")
            ]),
            _: 1 /* STABLE */
          }),
          _createVNode(Keycap, {
            width: "60px",
            height: "50px",
            top: "60px",
            left: "70px",
            "font-size": "11px",
            onClick: rotateBuffer
          }, {
            default: _withCtx(() => [
              _createTextVNode("ROTATE")
            ]),
            _: 1 /* STABLE */
          })
        ]),
        _: 1 /* STABLE */
      }),
      _createVNode(Keycap, {
        width: "60px",
        height: "50px",
        top: "60px",
        left: "160px",
        "font-size": "40px",
        onClick: applyBuffer
      }, {
        default: _withCtx(() => [
          _createTextVNode("➜")
        ]),
        _: 1 /* STABLE */
      })
    ]),
    _createVNode(ModuleStatusIndicator, {
      "is-armed": _unref(state).isArmed.value,
      "is-disarmed": _unref(state).isDisarmed.value,
      "strike-number": strikeNumber.value
    }, null, 8 /* PROPS */, ["is-armed", "is-disarmed", "strike-number"])
  ]))
}
}

})